import {
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputProps,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import makeStyles from "@mui/styles/makeStyles";
import { Close, Info, Star } from "@mui/icons-material";
import { Rating } from "@mui/material";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import MButton from "components/momentum/MButton";
import "moment-timezone";
import {
  useMetadataByCategory,
  useCountryMetadata,
  useEndCustomerContactById,
  useAccountById,
} from "components/summary/hooks";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useAxios } from "myAxios";
import { AuthProvider } from "pages";
import { FEATURE_KEY, PRIMARY_ROLES } from "constant";
import { Contact } from "types/summary";
import { useRouter } from "next/compat/router";

const useStyles = makeStyles(() => ({
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    padding: 24,
  },
  title: {
    fontSize: 16,
    color: "#171B1F",
    fontWeight: 500,
    display: "flex",
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  fieldHalf: {
    marginTop: 8,
    flexBasis: "48%",
    "&:nth-of-type(2n)": {
      marginLeft: "4%",
    },
  },
  fieldTitle: {
    color: "#121212",
    fontSize: 16,
    marginRight: 4,
    alignItems: "center",
    display: "flex",
    marginBottom: 8,
  },
  fieldText: {
    color: "#545454",
    fontSize: 14,
    alignSelf: "flex-end",
  },
  tooltipIcon: {
    color: "#00a0d1",
    fontSize: "1rem",
    marginLeft: 4,
  },
  tooltip: {
    marginLeft: 4,
  },
  roleContainer: {
    border: "1px solid #D9D9D9",
    borderRadius: 4,
    marginTop: 16,
  },
  roleTitle: {
    backgroundColor: "#F2F4F5",
    padding: 16,
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center",
    borderRadius: 4,
    display: "flex",
  },
  otherInput: {
    paddingTop: 8,
    paddingBottom: 8,
  },
}));

export interface ContactDialogProps {
  accountKey?: string;
  type: "Add New" | "Update";
  open: boolean;
  onClose: () => void;
  fullScreen?: boolean;
  version?: string;
  data: Partial<Contact> | undefined;
  accountId?: string;
  from?: string;
  page?: string;
  mutateFunc?: () => void;
}

interface Template {
  id: string;
  tooltip?: string;
  label: string;
  dropdownList?: { id: string; value: string }[];
  category?: string;
  type: "textbox" | "dropdownlist";
  required?: boolean;
  placeholder?: string;
  authKey?: FEATURE_KEY;
  disabled?: boolean;
  primaryFlag?: boolean;
}

const regMapping = {
  phoneNumber: /[\d-?\(?\)?]+$/g,
  email:
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/g,
};

const StyledTableCell = withStyles({
  head: {
    backgroundColor: "#F2F4F5",
    fontWeight: 700,
    fontSize: 16,
    padding: "12px 7px",
    borderBottom: "none",
    "&:first-of-type": {
      borderLeft: "1px solid #F2F4F5",
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
    },
    "&:last-of-type": {
      borderRight: "1px solid #F2F4F5",
      borderTopRightRadius: 4,
      borderbottomRightRadis: 4,
    },
  },
  body: {
    borderBottom: "none",
  },
})(TableCell);

const StyledTableRow = withStyles({
  root: {
    "&:nth-of-type(even)": {
      backgroundColor: "#F8F8F8",
    },
  },
})(TableRow);

const StyledRating = withStyles({
  iconFilled: {
    color: "#00a0d1",
  },
  iconHover: {
    color: "#00a0d1",
  },
})(Rating);

const ContactDialog: React.FC<ContactDialogProps> = ({
  accountKey,
  type,
  open,
  onClose,
  fullScreen = false,
  data,
  accountId = "fromRouterQuery",
  from,
  mutateFunc,
}) => {
  const axios = useAxios();
  const router = useRouter();
  const authDetail = useContext(AuthProvider);
  const classes = useStyles();
  const [updating, setUpdating] = useState(false);
  const { data: lovs } = useMetadataByCategory(
    "ACTIVITY_LEVEL,JOB_TITLE,DEPARTMENT,CONTACT_ROLE,CONTACT_LANGUAGE"
  );
  const { data: countries } = useCountryMetadata();
  const otherRef = useRef<InputProps>();
  const notSPUIInput = data?.spUiInputFlag !== "Y";

  const countryDropdown = useMemo(() => {
    return countries?.map((x) => ({ id: x.code, value: x.name }));
  }, [countries]);

  const { handleSubmit, control, reset, setValue } = useForm();

  const localAccountId =
    accountId === "fromRouterQuery" ? (router?.query.accountId as string) : accountId;

  const { mutate } = useEndCustomerContactById(localAccountId);

  const { data: account } = useAccountById(localAccountId);

  useEffect(() => {
    reset();
  }, [open]);

  const roleNames = data?.roleName?.split(",") ?? [];

  const primaryRoles = data?.primaryRoles?.split(",") ?? [];

  const disabledCondtion = notSPUIInput && type === "Update" ? true : false;
  const requiredCondition = !notSPUIInput || type === "Add New";

  const onSubmit = (submitData: { [key: string]: string }) => {
    if (accountKey) {
      let postData: { [key: string]: string } = {
        moduleName: data?.moduleName ?? "CONTACT_MASTER",
        ssCode: data?.ssCode ?? "Success Portal",
        spUiInputFlag: data?.spUiInputFlag ?? "Y",
        accountKey,
        optOutFlag: data?.optOutFlag ?? "N",
      };
      const roleNameArray: string[] = [];
      const primaryRoleArray: string[] = [];
      Object.entries(submitData).forEach(([k, value]) => {
        if (k.indexOf("roleName%%") >= 0) {
          if (value) {
            const [_a, roleName] = k.split("%%");
            roleNameArray.push(roleName);
          }
        } else if (k.indexOf("primaryRole%%") >= 0) {
          if (value) {
            const [_a, roleName] = k.split("%%");
            primaryRoleArray.push(roleName);
          }
        } else {
          postData = { ...postData, [k]: value };
        }
      });
      if (!roleNameArray.length && requiredCondition) {
        toast.error("Please check at least one role(primary or other)", {
          position: "top-center",
          autoClose: 3000,
        });
        return;
      }
      if (roleNameArray.some((r) => r === "Other")) {
        if (!!!(otherRef.current?.value as string)?.trim()) {
          toast.error("If select other role, then you should enter the role name besides other", {
            position: "top-center",
            autoClose: 3000,
          });
          return;
        } else if ((otherRef.current?.value as string)?.trim()?.length > 50) {
          toast.error("Maximum other name length 50", {
            position: "top-center",
            autoClose: 3000,
          });
          return;
        } else if ((otherRef.current?.value as string)?.includes(",")) {
          toast.error("Other role cannot contain comma(,)", {
            position: "top-center",
            autoClose: 3000,
          });
          return;
        }
      }
      postData = {
        ...postData,
        roleName: disabledCondtion
          ? data?.roleName || ""
          : roleNameArray
              .map((x) => (x === "Other" ? `Other%%${otherRef.current?.value}` : x))
              .join(","),
        primaryRoles: disabledCondtion ? data?.primaryRoles || "" : primaryRoleArray.join(","),
        contactModifiedTime: new Date().toUTCString(),
        contactCreateTime: new Date().toUTCString(),
      };
      setUpdating(true);

      axios
        .call(axios, `/api/dataservice/v1/accounts/${accountKey}/account-contacts`, {
          method: type === "Add New" ? "post" : "put",
          data: postData,
        })
        .then(() => {
          toast.success("Updated successfully", {
            position: "top-center",
            autoClose: 2000,
          });
          mutate();
          mutateFunc?.();
          onClose();
        })
        .catch((e) => {
          toast.error(e?.response?.data?.message || "Failed to update", {
            position: "top-center",
            autoClose: 3000,
          });
        })
        .finally(() => {
          setUpdating(false);
        });
    }
  };

  const onError = (errors: { [key: string]: any }) => {
    if (Object.keys(errors).length) {
      toast.error("Please correct the highlighted field(s)", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  let template: Template[] = [
    {
      id: "firstName",
      type: "textbox",
      label: "First Name",
      required: requiredCondition,
      placeholder: "required",
      tooltip: "Only English and some special characters like %, $, #, etc., are supported",
      disabled: disabledCondtion,
    },
    {
      id: "lastName",
      type: "textbox",
      label: "Last Name",
      required: requiredCondition,
      placeholder: "required",
      tooltip: "Only English and some special characters like %, $, #, etc., are supported",
      disabled: disabledCondtion,
    },
    {
      id: "activityLevel",
      type: "dropdownlist",
      label: "Activity Level",
      category: "ACTIVITY_LEVEL",
      authKey: FEATURE_KEY.EDIT_SUMMARY_CONTACT_ACTIVITYLEVEL,
    },
    {
      id: "phoneNumber",
      type: "textbox",
      label: "Phone Number",
      placeholder: "###-###-####",
      disabled: disabledCondtion,
    },
    {
      id: "email",
      type: "textbox",
      label: "Email",
      required: requiredCondition,
      placeholder: "required",
      disabled: type === "Update",
    },
    {
      id: "jobTitle",
      type: "dropdownlist",
      label: "Title",
      category: "JOB_TITLE",
      required: requiredCondition,
      disabled: disabledCondtion,
    },
    {
      id: "department",
      type: "dropdownlist",
      label: "Department",
      category: "DEPARTMENT",
      required: requiredCondition,
      disabled: disabledCondtion,
    },
    {
      id: "countryCode",
      type: "dropdownlist",
      label: "Country",
      dropdownList: countryDropdown,
      required: requiredCondition,
      disabled: disabledCondtion,
    },
    {
      id: "preferredLanguage",
      type: "dropdownlist",
      label: "Language",
      category: "CONTACT_LANGUAGE",
      required: requiredCondition,
      disabled: disabledCondtion,
    },
  ];

  template = template.filter(
    (x) => !x.authKey || (x.authKey && authDetail.features.includes(x.authKey))
  );

  const detailResult = template.map((a) => {
    return (
      <div
        key={a.id}
        className={`${classes.fieldHalf}`}
      >
        <p className={classes.fieldTitle}>
          {a.required ? <span style={{ color: "#FF5F58", fontSize: "1rem" }}>*</span> : null}
          {a.label}
          {a.tooltip ? (
            <Tooltip
              title={a.tooltip}
              arrow={true}
            >
              <Info className={classes.tooltipIcon} />
            </Tooltip>
          ) : null}
        </p>
        <Controller
          name={`${a.id}`}
          control={control}
          defaultValue={data?.[a.id as keyof Contact] ?? ""}
          rules={{
            required: {
              value: a.required ?? false,
              message: "This field is required!",
            },
            pattern: regMapping[a.id as keyof typeof regMapping]
              ? {
                  value: regMapping[a.id as keyof typeof regMapping],
                  message: `${a.id} is not valid`,
                }
              : undefined,
          }}
          render={({ field, fieldState }) => {
            switch (a.type) {
              case "textbox":
                return (
                  <TextField
                    {...field}
                    fullWidth={true}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    variant="outlined"
                    size="small"
                    placeholder={a.placeholder}
                    sx={{
                      "& .MuiInputBase-root": { height: 36 },
                    }}
                    disabled={a.disabled}
                    style={{ background: a.disabled ? "#eee" : "#fff" }}
                  />
                );
              case "dropdownlist":
                const list = a.dropdownList || lovs?.filter((x) => x.category === a.category);
                return (
                  <TextField
                    {...field}
                    fullWidth={true}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    select={true}
                    SelectProps={{
                      displayEmpty: true,
                      style: { color: field.value ? "inherit" : "#B4B6B8" },
                    }}
                    variant="outlined"
                    size="small"
                    placeholder={a.placeholder}
                    disabled={a.disabled}
                    sx={{
                      "& .MuiInputBase-root": { height: 36 },
                    }}
                    style={{ background: a.disabled ? "#eee" : "#fff" }}
                  >
                    <MenuItem value="">Please Select...</MenuItem>
                    {list?.map((x) => (
                      <MenuItem
                        key={x.id}
                        value={a.dropdownList ? x.id : x.value}
                      >
                        {x.value}
                      </MenuItem>
                    ))}
                  </TextField>
                );
            }
          }}
        />
      </div>
    );
  });

  return (
    <Dialog
      open={open}
      scroll={"body"}
      fullWidth={true}
      maxWidth={"md"}
      onClose={onClose}
      fullScreen={fullScreen}
    >
      <div className={classes.titleContainer}>
        <div>
          <p className={classes.title}>
            {type} Contact for {account?.accountName}
          </p>
        </div>
        {fullScreen ? null : (
          <IconButton
            onClick={onClose}
            size="small"
          >
            <Close style={{ color: "#121212", fillOpacity: 0.6 }} />
          </IconButton>
        )}
      </div>
      <DialogContent
        dividers={true}
        style={{
          paddingTop: 0,
          filter: updating ? "opacity(0.5)" : "opacity(1)",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <div className={classes.container}>{detailResult}</div>
          {!disabledCondtion ? (
            <div className={classes.roleContainer}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell
                        align="center"
                        style={{ width: "20%" }}
                      >
                        Primary
                      </StyledTableCell>
                      <StyledTableCell
                        align="left"
                        style={{ width: "80%" }}
                      >
                        <span
                          style={{
                            color: "#FF5F58",
                            fontSize: "1rem",
                          }}
                        >
                          *
                        </span>
                        Roles
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lovs
                      ?.filter((x) => x.category === "CONTACT_ROLE")
                      ?.map((a) => {
                        return (
                          <StyledTableRow
                            key={a.id}
                            className={classes.fieldHalf}
                            style={{ marginTop: 0, marginLeft: 16 }}
                          >
                            <StyledTableCell align="center">
                              {PRIMARY_ROLES.some((r) => r === a.value) ? (
                                <Controller
                                  name={`primaryRole%%${a.value}`}
                                  control={control}
                                  defaultValue={primaryRoles.some((x) => x === a.value) ? 1 : 0}
                                  render={({ field }) => {
                                    return (
                                      <StyledRating
                                        precision={1}
                                        max={1}
                                        icon={
                                          <Star
                                            style={{
                                              fontSize: "1rem !important",
                                            }}
                                          />
                                        }
                                        value={field.value}
                                        onChange={(_e, value) => {
                                          field.onChange(value);
                                          if (value) {
                                            setValue(`roleName%%${a.value}`, true);
                                          }
                                        }}
                                      />
                                    );
                                  }}
                                />
                              ) : null}
                            </StyledTableCell>
                            <StyledTableCell>
                              <Controller
                                name={`roleName%%${a.value}`}
                                control={control}
                                defaultValue={!!roleNames.find((x) => x.startsWith(a.value))}
                                render={({ field }) => {
                                  return (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <FormControlLabel
                                        label={`${a.value}`}
                                        control={
                                          <Checkbox
                                            checked={!!field.value}
                                            onChange={(e) => {
                                              field.onChange(e.target.checked);
                                              if (!e.target.checked) {
                                                setValue(`primaryRole%%${a.value}`, 0);
                                              }
                                            }}
                                            color="primary"
                                            disabled={disabledCondtion}
                                          />
                                        }
                                        style={{ marginRight: 0 }}
                                      />
                                      {a.value === "Other" && !!field.value ? (
                                        <TextField
                                          variant="outlined"
                                          size="small"
                                          style={{ marginLeft: 8 }}
                                          InputProps={{
                                            classes: {
                                              input: classes.otherInput,
                                            },
                                          }}
                                          required={true}
                                          inputRef={otherRef}
                                          defaultValue={
                                            roleNames
                                              .find((x) => x.includes("Other"))
                                              ?.split("%%")?.[1] ?? ""
                                          }
                                          placeholder="No comma"
                                          disabled={disabledCondtion}
                                        />
                                      ) : null}
                                      {a.description ? (
                                        <Tooltip
                                          title={a.description}
                                          arrow={true}
                                        >
                                          <Info className={classes.tooltipIcon} />
                                        </Tooltip>
                                      ) : null}
                                    </div>
                                  );
                                }}
                              />
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          ) : null}
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <MButton
              type="submit"
              buttonType="blue"
              buttonSize={36}
              onClick={handleSubmit(onSubmit, onError)}
              trackName={
                type === "Update"
                  ? "Update Contact"
                  : from === "Quick Link"
                    ? `Quick Link Add a Contact Submit`
                    : "Add a Contact"
              }
            >
              Submit
            </MButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDialog;
